import { gsap, ScrollTrigger } from '../lib/gsap';
import { prefersReducedMotion } from '../lib/lenis';

// Floating hero tiles: staggered fly-in on load (CSS transitions, so it can't
// get stuck if rAF is throttled), then scroll parallax + depth-based pointer
// parallax once settled.
export function initHeroTiles(): void {
  const tiles = Array.from(document.querySelectorAll<HTMLElement>('[data-tile]'));
  if (!tiles.length || prefersReducedMotion()) return;

  const EASE = 'cubic-bezier(.16,1,.3,1)';

  // Fly-in: remember each tile's authored transform/opacity, offset it, then
  // transition back with a stagger.
  tiles.forEach((t) => {
    const style = window.getComputedStyle(t);
    t.dataset.f = t.style.transform || '';
    t.dataset.o = style.opacity || '1';
    t.style.transition = 'none';
    t.style.opacity = '0';
    t.style.transform = `${getRotation(t)} translateY(46px) scale(.9)`;
  });

  window.setTimeout(() => {
    tiles.forEach((t, i) => {
      const delay = 0.3 + i * 0.07;
      t.style.transition = `opacity 1s ${EASE} ${delay}s, transform 1s ${EASE} ${delay}s`;
      t.style.opacity = t.dataset.o || '1';
      t.style.transform = getRotation(t);
    });
  }, 80);

  // Settle: clear inline transitions so GSAP caches the final transforms,
  // then hand over to the parallax layers.
  window.setTimeout(() => {
    tiles.forEach((t) => {
      t.style.transition = 'none';
      t.style.opacity = t.dataset.o || '1';
      t.style.transform = getRotation(t);
    });

    tiles.forEach((t) => {
      const depth = parseFloat(t.dataset.depth || '0.6');
      gsap.to(t, {
        yPercent: -58 * depth,
        ease: 'none',
        scrollTrigger: { trigger: '#top', start: 'top top', end: 'bottom top', scrub: 0.5 },
      });
    });
    ScrollTrigger.refresh();

    if (window.matchMedia('(pointer:fine)').matches) {
      const qx = tiles.map((t) => gsap.quickTo(t, 'x', { duration: 1.1, ease: 'power3' }));
      const qy = tiles.map((t) => gsap.quickTo(t, 'y', { duration: 1.1, ease: 'power3' }));
      window.addEventListener('pointermove', (e: PointerEvent) => {
        const nx = e.clientX / window.innerWidth - 0.5;
        const ny = e.clientY / window.innerHeight - 0.5;
        tiles.forEach((t, i) => {
          const depth = parseFloat(t.dataset.depth || '0.6');
          qx[i](nx * 64 * depth);
          qy[i](ny * 40 * depth);
        });
      });
    }
  }, 2700);
}

// The tiles' rotation lives in the stylesheet — read it once so inline
// animation transforms can compose with it.
function getRotation(t: HTMLElement): string {
  if (t.dataset.rot !== undefined) return t.dataset.rot;
  const inline = t.dataset.f;
  if (inline) {
    t.dataset.rot = inline;
    return inline;
  }
  const matrix = window.getComputedStyle(t).transform;
  if (!matrix || matrix === 'none') {
    t.dataset.rot = '';
    return '';
  }
  t.dataset.rot = matrix;
  return matrix;
}
